import { Children, cloneElement, useEffect, useRef } from 'react';

/*
  ModalOverlay — fondo + comportamiento accesible para modales.

  Resuelve javascript:S1082 / S6848 (y de paso S6847) en los overlays de modal:
  el clic en el fondo pasa a ser un atajo REDUNDANTE; las vías principales de
  cierre son Escape y el botón de cierre que ya tenga cada modal.

  Qué hace:
   · Escape cierra el modal.
   · Clic en el fondo cierra (solo si el clic EMPIEZA y ACABA en el fondo, así
     que arrastrar para seleccionar texto de un input y soltar fuera ya no lo cierra).
   · Al abrir, mueve el foco al modal (si nada de dentro lo tiene ya, p. ej. por autoFocus)
     y al cerrar lo devuelve al elemento que lo abrió.
   · Tab / Shift+Tab se quedan dentro del modal.
   · Añade role="dialog", aria-modal y aria-label al ÚNICO hijo (el panel del modal).

  Props:
   - onClose:   () => void
   - label:     string — nombre accesible del diálogo
   - className: clase del overlay (la que ya tenías: oa-modal-overlay, diploma-overlay, etc.)
   - style:     estilo opcional del overlay
   - children:  UN solo elemento: el panel del modal (el <div className="oa-modal"> de siempre)
*/

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const ModalOverlay = ({ onClose, label, className, style, children }) => {
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const pressedOnBackdrop = useRef(false);

  // Siempre llamamos a la última versión de onClose sin re-suscribir listeners
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Escape (a nivel de documento: funciona aunque el foco se haya ido al <body>)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Foco inicial y restauración al cerrar
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) {
      dialog.focus();
    }
    return () => {
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, []);

  // Trampa de foco
  const handleKeyDown = (e) => {
    if (e.key !== 'Tab') return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    const active = document.activeElement;
    // offsetParent === null → elemento oculto (display:none), no se puede enfocar
    const items = Array.from(dialog.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === active
    );

    if (items.length === 0) {
      e.preventDefault();
      dialog.focus();
      return;
    }

    const first = items[0];
    const last = items[items.length - 1];

    if (e.shiftKey && (active === first || active === dialog)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const panel = Children.only(children);

  return (
    <div
      className={className}
      style={style}
      role="presentation"
      onMouseDown={(e) => {
        pressedOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (pressedOnBackdrop.current && e.target === e.currentTarget) {
          onCloseRef.current?.();
        }
        pressedOnBackdrop.current = false;
      }}
      onKeyDown={handleKeyDown}
    >
      {cloneElement(panel, {
        ref: dialogRef,
        role: 'dialog',
        'aria-modal': true,
        'aria-label': label,
        tabIndex: -1,
        style: { outline: 'none', ...panel.props.style },
      })}
    </div>
  );
};

export default ModalOverlay;